import { useCallback, useEffect, useState } from 'react';
import { config } from '../../../app-confi/app-config';
import { usePrevious } from '../../../common/react-helpers';
import { Building } from '../../../confi-state';
import { ServiceManager } from '../../../services/service-manager';
import { alignment } from '../models/alignment.enum';
import { ColumnService } from './column-service';
import { bodiesEnum } from './models/bodies.enum';
import { coreType } from './models/core-type.enum';
import { coreTypesModel, defaultCore } from './models/core-types.model';
import { headType } from './models/head-type.enum';
import { headTypesModel } from './models/head-types.model';
import { plinthType } from './models/plinth-type.enum';
import { getDefaultPropsTower, PropsTower } from './models/props-config';
import styles from './Tower.module.css';
import buildingConfStyles from '../BuildingConf.module.css';
import { TowerService } from './tower.service';
import { FormControl, FormControlLabel, FormGroup, Switch, InputLabel, Select, MenuItem } from '@mui/material';
import { buildingType } from '../models/building-type.enum';
import { Point3 } from '../../../common/models/point.type';
import { InitialProps } from '../Common/BuildingInitialPropertiesDialog/init-props';
import { columnType } from './models/column-type.enum';
import { columnDefs, columnSubtype } from './models/column-definition';
import { segmentType } from './models/segment-type';
import { SectionJSON, UnitJ } from '../../../models/section-json';
import { ColDefOption } from '../../../app-confi/services/building-helper.service';

const buildingChange = (buildingProps: PropsTower, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } });
};

const Tower = ({
    currentBuilding,
    buildingScaleDimensions,
    previousBuildingDimensions,
    initProps,
    sectionJson,
    onChangeBuilding }: {
        currentBuilding: Building,
        buildingScaleDimensions: Point3,
        previousBuildingDimensions: Point3,
        initProps: InitialProps,
        sectionJson: SectionJSON,
        onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void
    }) => {

    const [buildingProps, setBuildingProps] = useState<PropsTower>(
        undefined
    );

    const [moosPlazaBays, setMoosPlazaBays] = useState<number>(undefined);
    const previousMoosPlazaBays = usePrevious<number>(moosPlazaBays);
    const [coreOptions, setCoreOptions] = useState<{ bays: number, type: coreType }[]>([]);
    const [moosPlazaBaysOptions, setMoosPlazaBaysOptions] = useState<{ bays: number, squareMeters: number }[]>([]);
    const [moosPlazaTopOptions, setMoosPlazaTopOptions] = useState<{
        forMoosPlazaBays: number,
        options: ColDefOption<columnType | headType>[]
    }[]>(
        []
    );
    const [headOptions, setHeadOptions] = useState<{ options: ColDefOption<columnType | headType>[] }[]>([
        { options: [] },
    ]);

    const previousBuildingScaleDimensions = usePrevious<Point3>(buildingScaleDimensions);

    const maxHeight = 70;
    const minHeight = 15;

    const recomputeColumns = useCallback((bp: PropsTower) => {
        const b = { ...bp };

        updateMoosPlazaTypeIfNeeded(b);
        const columnService = new ColumnService();

        b.parts = columnService.createPossibleColumnDefinitions(
            {
                selectedPlinthType: b.plinthType,
                selectedMiddleType: b.middleType,
                selectedCrownType: b.crownType,
                plinthFloors: b.plinthFloors,
                middleFloors: ServiceManager.buildingHelperService.getMiddleFloors(b.plinthType, b.buildingHeight, b.customPlinthHeight, b.crownFloors, b.plinthFloors, buildingType.tower),
                crownFloors: b.crownFloors,
                selectedMoosPlazaType: b.moosPlazaType,
                moosPlazaPosition: b.moosPlazaPosition,
                selectedMoosPlazaAlignment: b.moosPlazaAlignment,
                selectedHeadType: b.selectedHeadType,
                selectedCoreType: b.selectedCoreType,
                customPlinthHeight: b.customPlinthHeight,
                customPlinthWidth: b.customPlinthWidth,
            }
        );

        b.parts = ServiceManager.babylonService.towerBabylonService.mapPartsTo3D(
            b.parts,
            b.moosPlazaAlignment,
            b.moosPlazaPosition,
        );
        b.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(b.parts);
        if (b.parts) {
            const columns = b.parts.map(s => s.columns).flat().filter(c => c !== undefined);
            b.info = ServiceManager.buildingHelperService.computeColumnInfo(columns);
        }
        setBuildingProps({ ...b });
        buildingChange({ ...b }, currentBuilding, onChangeBuilding);
    }, [currentBuilding, onChangeBuilding])

    useEffect(() => {
        if (currentBuilding.props) {
            setBuildingProps({ ...currentBuilding.props as PropsTower });
            // migrate
            const columns = (currentBuilding.props as any).columns;
            if (columns !== undefined) {
                delete (currentBuilding.props as any).columns;
                recomputeColumns(currentBuilding.props as PropsTower);
            }
        }
    }, [currentBuilding.props, recomputeColumns])

    useEffect(() => {
        if (currentBuilding && !currentBuilding.props) {
            const props = getDefaultPropsTower(initProps);
            if (previousBuildingDimensions && (previousBuildingDimensions[0] !== 0 && previousBuildingDimensions[1] !== 0 && previousBuildingDimensions[2] !== 0)) {
                props.buildingHeight = ServiceManager.utilityService.keepInBounds(previousBuildingDimensions[1], minHeight, maxHeight);
            }
            recomputeColumns({ ...props });
        } else if (buildingProps && moosPlazaBays === undefined) {
            setMoosPlazaBays(TowerService.getColumnBays(buildingProps.moosPlazaType));
            const coreOptions = getCoreOptions();
            setCoreOptions(coreOptions);
            const options = getMoosPlazaBayData();
            setMoosPlazaBaysOptions(options);
            const topAvailableOptions = getMoosPlazaTopOptionsForAvailableBays(options.map(o => o.bays));
            setMoosPlazaTopOptions(topAvailableOptions);

            const headOptions = getHeadOptions();
            setHeadOptions([{
                options: headOptions
            }])

        }
    }, [buildingProps, currentBuilding, previousBuildingDimensions, onChangeBuilding, recomputeColumns])

    const getCoreOptions = () => {
        const coreBays = coreTypesModel.filter(s => s.type !== coreType.none).map(x => ({ bays: x.bays, type: x.type }));
        return coreBays;
    }

    const getHeadOptions = () => {
        const headDefinitions = columnDefs.filter(c =>
            c.columnSubtypes.includes(columnSubtype.head));
        const options = headDefinitions.map(colDef => {
            return ServiceManager.buildingHelperService.getColumnOptions(colDef, segmentType.middle, sectionJson);
        });
        return options;
    }

    const getMoosPlazaBayData = (): { bays: number, squareMeters: number }[] => {
        const s = columnDefs.map(s => {
            let bays = 0;
            let squareMeters = 0;
            if (s.columnSubtypes.includes(columnSubtype.moosplaza)) {
                bays = s.bays;
                const plinthDef = s.segmentDefinitions.find(sd => sd.type === segmentType.plinth);
                const sectionType = plinthDef.floorVariants[0].variants[0].sections[0];
                const sectionDescription = sectionJson.sections.find(s => s.sectionType === sectionType);
                if (sectionDescription) {
                    const moosPlazaAmenity = sectionJson.amenities.find(a => a.amenityType === sectionDescription.amenities[0].amenityType);
                    if (moosPlazaAmenity) {
                        squareMeters = moosPlazaAmenity.GFA;
                    }
                }
            }
            return { bays, squareMeters }
        });
        const baySqm = s.filter(s => s.bays !== 0);
        const uniqueBays = [];
        baySqm.forEach(bs => {
            if (!uniqueBays.find(s => s?.bays === bs.bays)) {
                uniqueBays.push(bs);
            }
        })
        return uniqueBays;
    }

    const getMoosPlazaTopOptionsForAvailableBays = (availableBays: number[]): {
        forMoosPlazaBays: number,
        options: ColDefOption<columnType | headType>[]
    }[] => {

        let res = availableBays.map(bay => {
            const moosPlazaColumnsOfBaySize = columnDefs.filter(c => c.columnSubtypes.includes(columnSubtype.moosplaza) && c.bays === bay)
            const s = moosPlazaColumnsOfBaySize.map(colDef => {
                return ServiceManager.buildingHelperService.getColumnOptions(colDef, segmentType.middle, sectionJson);
            });
            return { forMoosPlazaBays: bay, options: s };
        });
        return res;
    };

    useEffect(() => {
        if (moosPlazaBays) {
            if (previousMoosPlazaBays !== undefined && previousMoosPlazaBays !== moosPlazaBays) {
                const option = moosPlazaTopOptions.find(s => s.forMoosPlazaBays === moosPlazaBays);
                updateMoosPlazaType(option.options[0].type as columnType);
            }
        }
        if (moosPlazaBays === undefined && buildingProps && buildingProps.moosPlazaType !== columnType.none) {
            setMoosPlazaBays(TowerService.getColumnBays(buildingProps.moosPlazaType));
        }
    }, [moosPlazaBays])

    useEffect(() => {
        if (buildingScaleDimensions && buildingProps && (
            buildingScaleDimensions[1] !== previousBuildingScaleDimensions[1]
        ) && buildingScaleDimensions[1] !== 0
        ) {

            const b = { ...buildingProps };

            const updatedHeight = buildingProps.buildingHeight !== buildingScaleDimensions[1];

            if (updatedHeight) {
                b.buildingHeight = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[1], minHeight, maxHeight);
            }

            recomputeColumns(b);
        }
    }, [buildingScaleDimensions, previousBuildingScaleDimensions, buildingProps, recomputeColumns]);


    const updateMoosPlazaTypeIfNeeded = (bp: PropsTower) => {
        const availableMoosPlazaTypes = TowerService.getAvailableMoosPlazaTypes(bp.selectedHeadType, bp.selectedCoreType);
        if (!availableMoosPlazaTypes.some(m => m.type === bp.moosPlazaType)) {
            bp.moosPlazaType = availableMoosPlazaTypes[0].type;
        }
    }

    const handleChange = (e: React.FocusEvent<HTMLInputElement, Element>, type: 'height' | 'customPlinthHeight' | 'customPlinthWidth') => {
        const bp = { ...buildingProps };
        const value = +(e.target as any).value as number;
        switch (type) {
            case 'height':
                bp.buildingHeight = ServiceManager.utilityService.keepInBounds(value, minHeight, maxHeight);

                (e.target as HTMLInputElement).value = bp.buildingWidth.toString();
                if (bp.buildingHeight > config.highRiseLimit) {
                    const currentCore = coreTypesModel.find(core => core.type === bp.selectedCoreType);
                    if (!currentCore.highrise) {
                        bp.selectedCoreType = defaultCore;
                    }
                }
                break;
            case 'customPlinthHeight':
                bp.customPlinthHeight = value;
                break;
            case 'customPlinthWidth':
                bp.customPlinthWidth = value;
                break;
            default:
                alert('unknown value type provided');
                break;
        }
        recomputeColumns(bp);
    }

    const handleSelect = (e: any, type: 'moosPlazaType' | 'plinthType' | 'moosPlazaAlignment' | 'moosPlazaPosition' | 'selectedHeadType' | 'selectedCoreType' | 'plinthFloors') => {
        const bp = { ...buildingProps };
        const value = (e.target as any).value as any;
        switch (type) {
            case 'moosPlazaType':
                bp.moosPlazaType = value;
                const moosPlazaTypesAvailable = TowerService.getAvailableMoosPlazaTypes(bp.selectedHeadType, bp.selectedCoreType);
                const amongAvailable = TowerService.checkIfMoosPlazaIsAmongAvailable(moosPlazaTypesAvailable, bp.moosPlazaType);
                if (!amongAvailable) {
                    bp.moosPlazaType = moosPlazaTypesAvailable[0].type;
                }
                if (bp.moosPlazaType === columnType.none) {
                    bp.moosPlazaAlignment = alignment.center;
                }
                break;
            case 'moosPlazaAlignment':
                bp.moosPlazaAlignment = value;
                break;
            case 'plinthType':
                bp.plinthType = value;
                if (bp.plinthType === plinthType.Custom) {
                    bp.plinthFloors = 1;
                    if (!bp.customPlinthWidth) {
                        bp.customPlinthWidth = 10;
                    }
                    if (!bp.customPlinthHeight) {
                        bp.customPlinthHeight = 6.25;
                    }
                } else {
                    bp.plinthFloors = 2;
                    bp.customPlinthWidth = 0;
                    bp.customPlinthHeight = 0;
                }
                break;
            case 'plinthFloors':
                bp.plinthFloors = +value;
                recomputeColumns(bp);
                break;
            case 'moosPlazaPosition':
                bp.moosPlazaPosition = value;
                break;
            case 'selectedHeadType':
                bp.selectedHeadType = value;
                updateMoosPlazaTypeIfNeeded(bp);
                break;
            case 'selectedCoreType':
                bp.selectedCoreType = value;
                const core = coreTypesModel.find(core => core.type === bp.selectedCoreType);
                const head = headTypesModel.find(head => head.type === bp.selectedHeadType);

                bp.buildingLength = core.bays * config.towerBaySize + (head.width * 2);
                updateMoosPlazaTypeIfNeeded(bp);
                break;
            default:
                alert('unknown value type provided');
                break;
        }
        recomputeColumns(bp);
    }



    const updateCustomPlinth = () => {
        let bp = { ...buildingProps };
        if (bp.plinthType === plinthType.Custom) {
            bp.plinthType = plinthType.C;
            bp.plinthFloors = 2;
            bp.customPlinthWidth = 0;
            bp.customPlinthHeight = 0;
        } else {
            bp.plinthType = plinthType.Custom;
            bp.moosPlazaType = columnType.none;
            bp.plinthFloors = 1;
            if (!bp.customPlinthWidth) {
                bp.customPlinthWidth = 10;
            }
            if (!bp.customPlinthHeight) {
                bp.customPlinthHeight = config.towerFloorHeight * 2;
            }
        }
        recomputeColumns(bp);
    }

    const updateNoneMoosPlaza = () => {
        let bp = { ...buildingProps };
        if (bp.moosPlazaType === columnType.none) {
            bp.moosPlazaType = columnType.V_P3T_XS_M;
            setMoosPlazaBays(3);
        } else {
            bp.moosPlazaType = columnType.none;
            bp.moosPlazaAlignment = alignment.center;
        }
        recomputeColumns(bp);
    }

    const getValueOfMoosPlazaBaysOptions = () => {
        return TowerService.getColumnBays(buildingProps.moosPlazaType)
    }

    const updateMoosPlazaType = (type: columnType) => {
        let bp = { ...buildingProps };
        bp.moosPlazaType = type;
        recomputeColumns(bp);
    }

    const updateCore = (type: coreType) => {
        let bp = { ...buildingProps };
        bp.selectedCoreType = type;
        if (TowerService.getCoreBays(type) < TowerService.getColumnBays(bp.moosPlazaType)) {
            bp.moosPlazaType = columnType.V_P3T_XS_M;
            setMoosPlazaBays(3);
        }
        recomputeColumns(bp);
    }

    const updateNoneHead = () => {
        let bp = { ...buildingProps };
        if (bp.selectedHeadType === headType.none) {
            bp.selectedHeadType = headType.H_2C_XL;
        } else {
            bp.selectedHeadType = headType.none;
        }
        recomputeColumns(bp);
    }

    const getSelectFormCoreOptions = () => {
        return coreOptions && coreOptions.length > 0 &&
            <div className={buildingConfStyles.formControl}>
                <FormControl size="medium" fullWidth >
                    <InputLabel id={"core-selection-label"}>Kern</InputLabel>
                    <Select
                        labelId={"core-selection-label"}
                        id={"core-selection-id"}
                        data-testid="core-selection-id"
                        value={buildingProps.selectedCoreType}
                        onChange={(event) => { updateCore(event.target.value as coreType) }}
                        label="Kern"
                    >
                        {
                            coreOptions.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={'core-selection-' + index} key={'core-selection-' + index} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.bays} beuken</span>
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    const getSelectFormTopOptions = (bays: number) => {
        const moptions = moosPlazaTopOptions.find(o => o.forMoosPlazaBays === bays);
        return moptions && moptions.options.some(o => o.type === buildingProps.moosPlazaType) && moptions.forMoosPlazaBays === moosPlazaBays &&
            <div className={buildingConfStyles.formControl}>
                <FormControl size="medium" fullWidth >
                    <InputLabel id={"moosplaza-top-selection-label"}>Woningen boven entree</InputLabel>
                    <Select
                        labelId={"moosplaza-top-selection-label"}
                        id={"moosplaza-top-selection-id"}
                        data-testid="moosplaza-top-selection-id"
                        value={buildingProps.moosPlazaType}
                        onChange={(event) => { updateMoosPlazaType(event.target.value as columnType) }}
                        label="Woningen boven entree"
                    >
                        {
                            moptions.options.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={'moosplaza-top-selection-' + index} key={'moosplaza-top-selection-' + index} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    const getSelectFormHeadOptions = () => {
        const options = headOptions[0].options;
        return options && options.length &&
            <div className={buildingConfStyles.formControl}>
                <FormControl id={`head-selection-control`} size="medium" fullWidth >
                    <InputLabel id={`head-selection-label`}>Kop type</InputLabel>
                    <Select
                        labelId={`head-selection-label`}
                        id={`head-selection-id`}
                        data-testid={`head-selection-id`}
                        value={buildingProps.selectedHeadType}
                        onChange={(event) => { handleSelect(event, 'selectedHeadType') }}
                        label={'Kop type'}
                    >
                        {
                            options.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={`head-selection-${index}`} key={`head-selection-${index}`} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    const getMoosplazaBaysOptions = () => {
        const options = moosPlazaBaysOptions.filter(s => s.bays <= TowerService.getCoreBays(buildingProps.selectedCoreType));
        return <>
            {
                options.length > 0 &&
                <div className={buildingConfStyles.formControl}>
                    <FormControl size="medium" fullWidth>
                        <InputLabel id={"moosplaza-bays-selection-label"}>Entree breedte</InputLabel>
                        <Select
                            labelId={"moosplaza-bays-selection-label"}
                            id={"moosplaza-bays-selection-id"}
                            data-testid="moosplaza-bays-selection-id"
                            value={getValueOfMoosPlazaBaysOptions()}
                            onChange={(event) => {
                                setMoosPlazaBays(+event.target.value)
                            }}
                            label="Entree breedte"
                        >
                            {
                                options.map((bs, index) => {
                                    return (
                                        <MenuItem data-testid={'moosplaza-bays-selection-' + index} key={'moosplaza-bays-selection-' + index} value={bs.bays}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.bays} beuken</span> - {bs.squareMeters} ㎡
                                            </div>
                                        </MenuItem>
                                    )
                                })
                            }
                        </Select>
                    </FormControl>
                </div>
            }
        </>
    }

    return (
        <div className={styles.Tower} data-testid="Tower">
            {buildingProps && <>
                <div className="group">
                    <h2 className="greyed-out-text">Kern</h2>
                    {
                        getSelectFormCoreOptions()
                    }
                    <h2 className="greyed-out-text">Plint indelen</h2>
                    <FormGroup>
                        <FormControlLabel control={<Switch
                            checked={buildingProps.plinthType === plinthType.Custom}
                            onChange={updateCustomPlinth}
                        />} label="Maatwerk plint" />
                        <FormControlLabel disabled={buildingProps.plinthType === plinthType.Custom} control={<Switch
                            checked={buildingProps.moosPlazaType !== columnType.none}
                            onChange={updateNoneMoosPlaza}
                        />} label="Entree" />
                    </FormGroup>
                    {
                        buildingProps.moosPlazaType !== columnType.none && moosPlazaBays !== undefined && moosPlazaTopOptions.length > 0 &&
                        <>
                            {
                                getMoosplazaBaysOptions()
                            }
                            {
                                getSelectFormTopOptions(moosPlazaBays)
                            }
                            <div className={buildingConfStyles.formControl}>
                                <FormControl size="medium" fullWidth>
                                    <InputLabel id={"moosplaza-alignment-selection-label"}>Positie</InputLabel>
                                    <Select
                                        labelId={"moosplaza-alignment-selection-label"}
                                        id={"moosplaza-alignment-selection-id"}
                                        data-testid="moosplaza-alignment-selection-id"
                                        value={buildingProps.moosPlazaAlignment}
                                        onChange={(event) => {
                                            handleSelect(event, 'moosPlazaAlignment')
                                        }}
                                        label="Positie"
                                    >
                                        <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.left}>
                                            Links
                                        </MenuItem>
                                        <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.center}>
                                        Midden
                                        </MenuItem>
                                        <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.right}>
                                            Rechts
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            <div className={buildingConfStyles.formControl}>
                                <FormControl size="medium" fullWidth>
                                    <InputLabel id={"moosplaza-body-selection-label"}>Deel</InputLabel>
                                    <Select
                                        labelId={"moosplaza-body-selection-label"}
                                        id={"moosplaza-body-selection-id"}
                                        data-testid="moosplaza-body-selection-id"
                                        value={buildingProps.moosPlazaPosition}
                                        onChange={(event) => {
                                            handleSelect(event, 'moosPlazaPosition')
                                        }}
                                        label="Deel"
                                    >
                                        <MenuItem data-testid={'moosplaza-body-selection-' + 0} value={bodiesEnum.A}>
                                            A
                                        </MenuItem>
                                        <MenuItem data-testid={'moosplaza-body-selection-' + 0} value={bodiesEnum.B}>
                                            B
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </>
                    }
                    <div className={buildingConfStyles.formControl}>
                        {
                            buildingProps.plinthType === plinthType.Custom ?
                                <dl>
                                    <dt>
                                        Plint verdiepingen</dt>
                                    <dd>
                                        {buildingProps.plinthFloors}
                                    </dd>
                                </dl>
                                :
                                <div className={buildingConfStyles.formControl}>
                                    <FormControl size="medium" fullWidth>
                                        <InputLabel id={"plint-floors-selection-label"}>Plint verdiepingen</InputLabel>
                                        <Select
                                            labelId={"plint-floors-selection-label"}
                                            id={"plint-floors-selection-id"}
                                            data-testid="plint-floors-selection-id"
                                            value={buildingProps.plinthFloors}
                                            onChange={(e) => {
                                                handleSelect(e as any, 'plinthFloors')
                                            }}
                                            label="Plint verdiepingen"
                                        >
                                            <MenuItem data-testid={'alignment-selection-' + 0} value={1}>
                                                1
                                            </MenuItem>
                                            <MenuItem data-testid={'alignment-selection-' + 0} value={2}>
                                                2
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                        }
                    </div>
                    {buildingProps.plinthType === plinthType.Custom &&
                        <div >
                            <label>
                                Custom Plint Breedte (m)
                                <input type="number" value={buildingProps.customPlinthWidth}
                                    onChange={(e) => { setBuildingProps({ ...buildingProps, customPlinthWidth: +e.target.value }) }}
                                    onBlur={(e) => handleChange(e, 'customPlinthWidth')} placeholder="breedte"></input>
                            </label>
                            <label>
                                Custom Plint Hoogte (m)
                                <input type="number" value={buildingProps.customPlinthHeight}
                                    onChange={(e) => { setBuildingProps({ ...buildingProps, customPlinthHeight: +e.target.value }) }}
                                    onBlur={(e) => handleChange(e, 'customPlinthHeight')} placeholder="hoogte"></input>
                            </label>
                        </div>
                    }
                </div>
                <hr></hr>
                <div className="group">
                    <div className={buildingConfStyles.formControl}>
                        <FormGroup>

                            <FormControlLabel control={<Switch
                                checked={buildingProps.selectedHeadType !== headType.none}
                                onChange={updateNoneHead}
                            />} label="Koppen" />
                        </FormGroup>
                        {
                            buildingProps.selectedHeadType !== headType.none && getSelectFormHeadOptions()
                        }
                    </div>
                </div>
            </>}
        </div>
    )
};

export default Tower;
